/**
 * QA Panel — public API.
 *
 * The only thing the lobby imports is `initQaPanel`. The call site MUST be
 * wrapped in a build-flag check (`process.env.ENABLE_QA_PANEL === 'true'`)
 * so that production bundles dead-strip the entire feature module via the
 * webpack DefinePlugin → terser dead-code elimination pipeline.
 *
 * Runtime activation (URL param, localStorage, keyboard shortcut) is handled
 * inside this module — the call site doesn't need to know.
 */

import {
    bindActivationLongPress,
    bindActivationShortcut,
    shouldAutoOpenOnBoot,
} from './bridge/activation';
import { QaPanelBridge } from './bridge/QaPanelBridge';

let bridge: QaPanelBridge | null = null;

export function initQaPanel(): void {
    if (typeof window === 'undefined') return;
    if (bridge) return; // already initialized

    bridge = new QaPanelBridge();
    bridge.init();

    // Toggle on Ctrl/Cmd+Shift+Q. Always available, also counts as a user
    // gesture so the popup blocker won't intervene.
    bindActivationShortcut(() => bridge?.togglePopup());

    // On touch devices, a 5-second long-press on the StatusBar settings button
    // toggles the in-DOM overlay (no navigation, session preserved). No-op on
    // desktop, which keeps the keyboard shortcut.
    bindActivationLongPress(() => bridge?.togglePopup());

    // Auto-open if URL param or localStorage flag is set. Won't survive the
    // popup blocker without a gesture, so the bridge surfaces a floating
    // "Open QA Panel" button on failure.
    if (shouldAutoOpenOnBoot()) {
        bridge.openPopup();
    }
}
